import {
  CheckSquare2,
  Building2,
  Globe,
  Lock,
  Play,
  Clock,
  AlertTriangle,
  GitBranch,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';

/**
 * Sidebar navigation for Tasks page
 * Follows default shadcn styling for consistency
 */
export const TasksSidebar = ({
  projectId,
  space,
  currentView,
  librarySubView,
  onNavigate,
  outstandingCount = 0,
}) => {
  const [libraryExpanded, setLibraryExpanded] = useState(
    currentView === 'library'
  );

  const navigation = [
    {
      name: 'Overview',
      items: [
        {
          name: 'Tasks',
          view: 'tasks',
          icon: CheckSquare2,
        },
        {
          name: 'Live Runs',
          view: 'live-runs',
          icon: Play,
        },
      ],
    },
    {
      name: 'Library',
      expandable: true,
      items: [
        {
          name: 'Company',
          view: 'library',
          subView: 'company',
          icon: Building2,
          description: 'Company processes',
        },
        {
          name: 'Space',
          view: 'library',
          subView: 'space',
          icon: Globe,
          description: 'Space workflows & pages',
        },
        {
          name: 'Personal',
          view: 'library',
          subView: 'personal',
          icon: Lock,
          description: 'Private items',
        },
      ],
    },
    {
      name: 'Management',
      items: [
        {
          name: 'Schedules',
          view: 'schedules',
          icon: Clock,
        },
        {
          name: 'Outstanding',
          view: 'outstanding',
          icon: AlertTriangle,
          badge: outstandingCount,
        },
      ],
    },
  ];

  const handleItemClick = (item) => {
    if (item.subView) {
      onNavigate(item.view, item.subView);
    } else {
      onNavigate(item.view);
    }
  };

  const handleLibraryHeaderClick = () => {
    setLibraryExpanded(!libraryExpanded);
    // When clicking the Library header, navigate to library with default subView
    if (!libraryExpanded) {
      onNavigate('library', librarySubView || 'space');
    }
  };

  return (
    <nav className="flex flex-col h-full bg-background border-r">
      {/* Navigation Groups */}
      <div className="flex-1 overflow-y-auto p-3 space-y-6">
        {navigation.map((group) => (
          <div key={group.name}>
            {group.expandable ? (
              // Library group with expandable header
              <>
                <button
                  onClick={handleLibraryHeaderClick}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors',
                    currentView === 'library'
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                >
                  <GitBranch className="h-3.5 w-3.5" />
                  <span className="flex-1 text-left">{group.name}</span>
                  {libraryExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
                {libraryExpanded && (
                  <div className="mt-1 ml-3 space-y-1 border-l pl-3">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive =
                        currentView === item.view &&
                        librarySubView === item.subView;

                      return (
                        <button
                          key={item.name}
                          onClick={() => handleItemClick(item)}
                          className={cn(
                            'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                            isActive
                              ? 'bg-accent text-accent-foreground'
                              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          )}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1 text-left">{item.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              // Regular group
              <>
                <h3 className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                  {group.name}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentView === item.view;
                    const showBadge = item.badge > 0;

                    return (
                      <button
                        key={item.name}
                        onClick={() => handleItemClick(item)}
                        className={cn(
                          'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors',
                          isActive
                            ? 'bg-accent text-accent-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="flex-1 text-left">{item.name}</span>
                        {showBadge && (
                          <Badge variant="secondary">
                            {item.badge}
                          </Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </nav>
  );
};

export default TasksSidebar;
